import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
  KebabButton,
} from '@src/components/common/contextMenuStyles';
import InfoIcon from '@src/svgs/InfoIcon';
import MoreVertIcon from '@src/svgs/MoreVertIcon';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useOutsideAlerter } from '../../transcript/useClickOutside';

import type { FC, MouseEvent } from 'react';

type Props = {
  onDetails: () => void;
};

const MENU_WIDTH = 180;
const MENU_HEIGHT = 60;

// Kebab actions menu for a clip row. Currently just "Details"; structured like
// StoryRowActionsMenu so more clip actions can be added later.
const ClipRowActionsMenu: FC<Props> = ({ onDetails }) => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setPos(null), []);
  useOutsideAlerter(portalRef, close);

  useEffect(() => {
    if (!pos) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [pos, close]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (pos) {
      close();
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setPos(
      clampMenuToViewport(
        rect.right - MENU_WIDTH,
        rect.bottom + 4,
        MENU_WIDTH,
        MENU_HEIGHT,
      ),
    );
  };

  const wrap = (cb: () => void) => (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    close();
    cb();
  };

  return (
    <>
      <KebabButton
        onClick={handleClick}
        data-testid="clip-row-actions-trigger"
        aria-label="Clip actions"
      >
        <MoreVertIcon />
      </KebabButton>
      {pos &&
        createPortal(
          <ContextMenuSurface
            ref={portalRef}
            style={{ top: pos.y, left: pos.x }}
            data-testid="clip-row-actions-menu"
          >
            <ContextMenuItem
              onClick={wrap(onDetails)}
              data-testid="clip-action-details"
            >
              <InfoIcon /> Details
            </ContextMenuItem>
          </ContextMenuSurface>,
          document.body,
        )}
    </>
  );
};

export default ClipRowActionsMenu;
